import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import BulkActionBar from "@/components/BulkActionBar";
import useBulkSelection from "@/hooks/useBulkSelection";
import SuperAdminStoreGate from "@/components/SuperAdminStoreGate";
import { useAuth, isSuperAdmin } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, FolderTree, ImageIcon, ChevronRight, ChevronDown, GripVertical, Filter, X, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const emptyForm = { name: "", slug: "", parent_id: "", section: "", description: "", image_url: "", sort_order: 0, is_active: true };

export default function Categories() {
  // Sprint 79 — Categories management is Super Admin only. Non-SA users
  // hitting this URL directly are redirected to their dashboard.
  const { user } = useAuth();
  if (user && !isSuperAdmin(user)) {
    const slug = user.store_slug || "platform";
    return <Navigate to={`/admin/${slug}/dashboard`} replace />;
  }
  return (
    <SuperAdminStoreGate
      pageTitle="Categories & Subcategories"
      pageSubtitle="Platform-wide category tree. Store Admins can select these when adding products."
      autoPickPrimary
    >
      <CategoriesInner />
    </SuperAdminStoreGate>
  );
}

function CategoriesInner() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({}); // id → bool
  const [q, setQ] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    level: "",   // "" | "parent" | "child"
    status: "",  // "" | "active" | "inactive"
  });

  const sel = useBulkSelection();

  const filterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== "" && v !== null).length,
    [filters]
  );

  const fetchRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories?limit=100");
      setRows(data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); }, []);

  const openCreate = (parent_id = "") => { setEditing(null); setForm({ ...emptyForm, parent_id }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...r, parent_id: r.parent_id || "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, parent_id: form.parent_id || null, sort_order: Number(form.sort_order || 0) };
      if (editing) await api.patch(`/categories/${editing.id}`, payload);
      else await api.post("/categories", payload);
      toast.success(editing ? "Category updated" : "Category created");
      setOpen(false); fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/categories/${r.id}`); toast.success("Deleted"); fetchRows(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  // Bulk actions
  const runBulk = async (fn, verb, past) => {
    const ids = sel.ids;
    if (ids.length === 0) return;
    if (verb === "delete" && !window.confirm(`Delete ${ids.length} selected categor${ids.length > 1 ? "ies" : "y"}? This cannot be undone.`)) return;
    const t = toast.loading(`${verb === "delete" ? "Deleting" : verb === "activate" ? "Activating" : "Deactivating"} ${ids.length}…`);
    const results = await Promise.allSettled(ids.map(fn));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = ids.length - ok;
    toast.dismiss(t);
    if (fail === 0) toast.success(`${past} ${ok}`);
    else toast.warning(`${ok} ok · ${fail} failed`);
    sel.clear();
    fetchRows();
  };
  const bulkDelete     = () => runBulk((id) => api.delete(`/categories/${id}`), "delete", "Deleted");
  const bulkActivate   = () => runBulk((id) => api.patch(`/categories/${id}`, { is_active: true }),  "activate",   "Activated");
  const bulkDeactivate = () => runBulk((id) => api.patch(`/categories/${id}`, { is_active: false }), "deactivate", "Deactivated");

  // Build parent → children map
  const tree = useMemo(() => {
    const parents = rows.filter((r) => !r.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return parents.map((p) => ({
      ...p,
      children: rows
        .filter((c) => c.parent_id === p.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    }));
  }, [rows]);

  // Unique existing section labels for the datalist autocomplete.
  const sectionSuggestions = useMemo(() => {
    const s = new Set();
    for (const r of rows) if (r.section) s.add(r.section);
    return [...s].sort();
  }, [rows]);

  const filteredTree = useMemo(() => {
    const term = q.trim().toLowerCase();
    const statusOk = (c) =>
      filters.status === "" ||
      (filters.status === "active" ? c.is_active !== false : c.is_active === false);

    let result = tree
      .map((p) => {
        const hitParent = !term || p.name.toLowerCase().includes(term);
        const kids = p.children.filter(
          (c) => (!term || c.name.toLowerCase().includes(term)) && statusOk(c)
        );
        if (filters.level === "child") {
          return kids.length > 0 ? { ...p, children: kids, _hideParent: true } : null;
        }
        if (filters.level === "parent") {
          return hitParent && statusOk(p) ? { ...p, children: [] } : null;
        }
        // Level: any — show parent if parent or any child matches (respecting status).
        const parentPasses = hitParent && statusOk(p);
        return parentPasses || kids.length > 0
          ? { ...p, children: parentPasses ? p.children.filter(statusOk) : kids }
          : null;
      })
      .filter(Boolean);
    return result;
  }, [tree, q, filters]);

  const totalCats = rows.length;
  const parentCount = tree.length;
  const subCount = totalCats - parentCount;

  const visibleIds = useMemo(
    () => filteredTree.flatMap((p) => [...(p._hideParent ? [] : [p.id]), ...p.children.map((c) => c.id)]),
    [filteredTree]
  );

  return (
    <div className="p-4 md:p-8 pb-24" data-testid="categories-page">
      <PageHeader
        title="Categories"
        subtitle="Structure your catalog into Category → Sub Category → Product."
        actions={
          <div className="flex gap-2">
            <Button
              variant={filtersOpen || filterCount > 0 ? "default" : "outline"}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="cats-filter-toggle"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters{filterCount > 0 ? ` · ${filterCount}` : ""}
            </Button>
            <Button onClick={() => openCreate("")} className="btn-primary-glow" data-testid="add-category-btn">
              <Plus className="w-4 h-4 mr-2" />New category
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories…" className="pl-3" data-testid="cat-search" />
        </div>
        <div className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{parentCount}</span> categories · <span className="font-semibold text-foreground">{subCount}</span> sub categories
        </div>
      </div>

      {filtersOpen && (
        <div className="mb-4 rounded-2xl border bg-card p-4 space-y-3" data-testid="cats-filter-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px]">Level</Label>
              <Select value={filters.level || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, level: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-level"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any level</SelectItem>
                  <SelectItem value="parent">Parent categories only</SelectItem>
                  <SelectItem value="child">Sub categories only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px]">Status</Label>
              <Select value={filters.status || "__any"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__any" ? "" : v }))}>
                <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">Any</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button size="sm" variant="ghost" onClick={() => setFilters({ level: "", status: "" })} data-testid="filter-clear">
                <X className="w-3.5 h-3.5 mr-1" />Clear all
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl border bg-card overflow-hidden">
        <div className="grid grid-cols-[40px_1fr_140px_100px_120px] px-4 md:px-6 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b bg-secondary/40">
          <div>
            <Checkbox
              checked={visibleIds.length > 0 && visibleIds.every((id) => sel.isSelected(id))
                ? true
                : visibleIds.some((id) => sel.isSelected(id)) ? "indeterminate" : false}
              onCheckedChange={() => sel.toggleAll(visibleIds)}
              aria-label="Select all"
              data-testid="cat-select-all"
            />
          </div>
          <div>Category</div><div className="text-right">Order</div><div className="text-right">Status</div><div className="text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-6 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-12 rounded-lg bg-secondary/50 animate-pulse" />)}
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-12">No categories match your filters.</div>
        ) : (
          <div className="divide-y" data-testid="cat-tree">
            {filteredTree.map((p) => {
              const isOpenNode = expanded[p.id] !== false; // default open
              const showParent = !p._hideParent;
              return (
                <div key={p.id} data-testid={`cat-row-${p.id}`}>
                  {/* Parent row */}
                  {showParent && (
                    <div className={`grid grid-cols-[40px_1fr_140px_100px_120px] items-center px-4 md:px-6 py-3 hover:bg-secondary/30 group ${sel.isSelected(p.id) ? "bg-primary/5" : ""}`}>
                      <div>
                        <Checkbox
                          checked={sel.isSelected(p.id)}
                          onCheckedChange={() => sel.toggle(p.id)}
                          aria-label="Select"
                          data-testid={`cat-select-${p.id}`}
                        />
                      </div>
                      <button
                        onClick={() => setExpanded((s) => ({ ...s, [p.id]: !isOpenNode }))}
                        className="flex items-center gap-3 text-left min-w-0"
                        data-testid={`cat-toggle-${p.id}`}
                      >
                        {p.children.length > 0
                          ? (isOpenNode ? <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />)
                          : <span className="w-4 h-4 shrink-0" />}
                        <div className="w-10 h-10 rounded-lg bg-secondary overflow-hidden grid place-items-center shrink-0">
                          {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <FolderTree className="w-4 h-4 text-primary" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold truncate flex items-center gap-2">
                            <span className="truncate">{p.name}</span>
                            {p.section && (
                              <Badge variant="outline" className="text-[9.5px] font-medium bg-primary/5 border-primary/30 text-primary uppercase tracking-wider" data-testid={`cat-section-badge-${p.id}`}>
                                {p.section}
                              </Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-muted-foreground truncate">{p.children.length} sub · {p.slug}</div>
                        </div>
                      </button>
                      <div className="text-right text-xs font-mono text-muted-foreground">{p.sort_order || 0}</div>
                      <div className="text-right">
                        <Badge variant={p.is_active ? "default" : "secondary"} className={p.is_active ? "bg-accent hover:bg-accent" : ""}>
                          {p.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openCreate(p.id)} title="Add sub category" data-testid={`add-sub-${p.id}`}><Plus className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-cat-${p.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => del(p)} data-testid={`delete-cat-${p.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </div>
                  )}

                  {/* Children */}
                  {(isOpenNode || p._hideParent) && p.children.map((c) => (
                    <div key={c.id} className={`grid grid-cols-[40px_1fr_140px_100px_120px] items-center px-4 md:px-6 py-2.5 bg-secondary/10 hover:bg-secondary/40 border-t ${sel.isSelected(c.id) ? "bg-primary/5" : ""}`} data-testid={`cat-row-${c.id}`}>
                      <div>
                        <Checkbox
                          checked={sel.isSelected(c.id)}
                          onCheckedChange={() => sel.toggle(c.id)}
                          aria-label="Select"
                          data-testid={`cat-select-${c.id}`}
                        />
                      </div>
                      <div className="flex items-center gap-3 min-w-0 pl-8">
                        <GripVertical className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <div className="w-8 h-8 rounded-md bg-secondary overflow-hidden grid place-items-center shrink-0">
                          {c.image_url ? <img src={c.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-3.5 h-3.5 text-muted-foreground" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{c.name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{c.slug}</div>
                        </div>
                      </div>
                      <div className="text-right text-xs font-mono text-muted-foreground">{c.sort_order || 0}</div>
                      <div className="text-right">
                        <Badge variant={c.is_active ? "default" : "secondary"} className={c.is_active ? "bg-accent hover:bg-accent" : ""}>
                          {c.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-cat-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => del(c)} data-testid={`delete-cat-${c.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BulkActionBar
        count={sel.count}
        onClear={sel.clear}
        actions={[
          { label: "Activate",   icon: <CheckCircle2 className="w-3.5 h-3.5" />, onClick: bulkActivate,   testId: "bulk-activate" },
          { label: "Deactivate", icon: <Ban className="w-3.5 h-3.5" />,          onClick: bulkDeactivate, testId: "bulk-deactivate" },
          { label: "Delete",     icon: <Trash2 className="w-3.5 h-3.5" />,       onClick: bulkDelete, tone: "destructive", testId: "bulk-delete" },
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] p-0 flex flex-col" data-testid="category-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0"><DialogTitle className="font-display text-2xl">{editing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto if blank" data-testid="cat-slug" />
              </div>
            </div>

            <div>
              <Label>Parent category</Label>
              <Select value={form.parent_id || "__none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="cat-parent"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— root category —</SelectItem>
                  {tree
                    .filter((r) => r.id !== editing?.id)
                    .map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground mt-1">Leave empty to make this a top-level category.</div>
            </div>

            <ImageUpload
              value={form.image_url || ""}
              onChange={(v) => setForm({ ...form, image_url: v })}
              label="Category image"
              hint="Shown on the customer storefront category rail. Square works best."
              aspect="square"
              testId="cat-image"
            />

            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cat-desc" />
            </div>

            <div>
              <Label className="flex items-center justify-between">
                <span>Section header</span>
                <span className="text-[10px] text-muted-foreground font-normal">Optional · shown on storefront /category</span>
              </Label>
              <Input
                list="cat-section-suggestions"
                value={form.section || ""}
                onChange={(e) => setForm({ ...form, section: e.target.value })}
                placeholder="e.g. Grocery & Kitchen, Snacks & Drinks, Personal Care…"
                data-testid="cat-section"
              />
              <datalist id="cat-section-suggestions">
                {sectionSuggestions.map((s) => <option key={s} value={s} />)}
              </datalist>
              <p className="text-[10.5px] text-muted-foreground mt-1">
                Categories with the same section header get grouped together on the storefront (Blinkit-style).
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="cat-order" />
              </div>
              <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Active</div>
                  <div className="text-[11px] text-muted-foreground">Visible to customers</div>
                </div>
                <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="cat-active" />
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t bg-card shrink-0">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} className="btn-primary-glow" data-testid="save-cat-btn">
              {saving ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
